/**
 * Labstep
 *
 * @module state/middleware/entity-identifier-resolver
 * @desc Middleware to resolve GUID identifiers to IDs for entities that use numeric IDs
 */

import type { Entity } from 'labstep-web/models/entity.model';
import { getIdAttribute } from 'labstep-web/services/schema/helpers';

/**
 * Resolves a GUID identifier to its corresponding numeric ID
 * @param entityName The name of the entity
 * @param identifier The identifier to resolve (can be GUID or ID)
 * @param state The current Redux state
 * @returns The resolved identifier (ID or original if not found/not applicable)
 */
const resolveIdentifier = (
  entityName: string,
  identifier: string | number,
  state: any,
): string | number | undefined => {
  // Only resolve if identifier is a string containing hyphens (GUID format)
  // and the entity uses numeric IDs
  if (
    !identifier ||
    typeof identifier !== 'string' ||
    !identifier.includes('-')
  ) {
    return identifier;
  }

  const entityPrimaryKey = getIdAttribute(entityName) as
    | 'id'
    | 'guid';

  if (entityPrimaryKey !== 'id') {
    return identifier;
  }

  // Look up the entity by GUID and return its ID
  const entities = state.entities[entityName]?.byId;
  if (!entities) {
    return undefined;
  }

  const entity = Object.values(entities as Entity[]).find(
    (entity: Entity) => entity.guid === identifier,
  );

  return entity?.id;
};

/**
 * Redux middleware that resolves GUID identifiers to IDs for entity actions
 */
export const entityIdentifierResolver =
  (store: any) => (next: any) => (action: any) => {
    // Only process actions that have meta.entityName and meta.identifier
    if (!action?.meta?.entityName || !action?.meta?.identifier) {
      return next(action);
    }

    const { entityName, identifier } = action.meta;
    const state = store.getState();

    // Resolve the identifier
    const resolvedIdentifier = resolveIdentifier(
      entityName,
      identifier,
      state,
    );

    // If the identifier was resolved to something different, update the action
    if (resolvedIdentifier && resolvedIdentifier !== identifier) {
      const updatedAction = {
        ...action,
        meta: {
          ...action.meta,
          identifier: resolvedIdentifier,
        },
      };

      return next(updatedAction);
    }

    return next(action);
  };
